import React, { useState, useEffect } from "react";
import { Table, Spin, Tag } from "antd";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import Box from "@mui/material/Box";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import moment from "moment";
import SidebarNav from "../sidebar";
import RoleForm from "./RoleForm";
import { getRoles, deleteRole } from "../../../service/api";
import { usePermission } from "../../../hooks/usePermission";
import CustomPagination from "../Pagination/pagination";
import FilterPanel from "../commoncomponent/FilterPanel";
import TableToolbar from "../commoncomponent/TableToolbar";
import { useTableSettings } from "../../../hooks/useTableSettings";
import { sanitizeInput } from "../../../config/validation";
import { triggerDownload } from "../../../config/downloadUtils";

const styleDelete = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 4,
};

const formatDateTime = (val) =>
  val ? moment(val).format("DD/MM/YYYY HH:mm") : "-";

const ALL_COLUMNS = [
  {
    key: "name",
    title: "Role Name",
    defaultVisible: true,
    render: (r) => (
      <span>
        {r?.name || "-"}
        {r?.roleType === 1 && (
          <Tag color="gold" style={{ marginLeft: "8px" }}>
            Super Admin
          </Tag>
        )}
      </span>
    ),
  },
  {
    key: "description",
    title: "Description",
    defaultVisible: true,
    render: (r) => r?.description || "-",
  },
  {
    key: "isActive",
    title: "Status",
    defaultVisible: true,
    render: (r) => (
      <Tag color={r?.isActive ? "green" : "red"}>
        {r?.isActive ? "Active" : "Inactive"}
      </Tag>
    ),
  },
  {
    key: "userCount",
    title: "Users",
    defaultVisible: true,
    render: (r) => r?.userCount || 0,
  },
  {
    key: "createdAt",
    title: "Created",
    defaultVisible: true,
    render: (r) => formatDateTime(r?.createdAt),
  },
  {
    key: "updatedAt",
    title: "Updated",
    defaultVisible: false,
    render: (r) => formatDateTime(r?.updatedAt),
  },
];

const RoleManagement = () => {
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { canCreate, canUpdate, canDelete } = usePermission();

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({
    storageKey: "role-list",
    allColumns: ALL_COLUMNS,
  });

  useEffect(() => {
    fetchRoles();
  }, [page, pageSize, searchTerm]);

  // Reset to page 1 whenever filters or page size change.
  useEffect(() => {
    setPage(1);
  }, [pageSize, searchTerm]);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const body = {
        page,
        sizePerPage: pageSize,
        pagination: "true",
      };
      if (searchTerm) {
        body.search = searchTerm;
      }
      const res = await getRoles(body);
      if (res?.status && res?.data) {
        setRoleList(res.data.result || []);
        setTotal(res.data.total || 0);
      }
    } catch (e) {
      toast.error("Failed to fetch roles", { toastId: 'roles-fetch-error' });
    }
    setLoading(false);
  };

  const handleDownload = async (format) => {
    setDownloadLoading(true);
    try {
      const body = { pagination: false };
      if (searchTerm) body.search = searchTerm;
      const res = await getRoles(body);
      const result = res?.data?.result ?? [];
      if (!result.length) { toast.warning("Nothing to download", { toastId: 'nothing-to-download' }); return; }
      const cols = ALL_COLUMNS.filter((c) => visibleColumnKeys.includes(c.key));
      triggerDownload(format, result, cols, "roles");
    } catch (e) {
      toast.error("Download failed", { toastId: 'download-failed' });
    }
    setDownloadLoading(false);
  };

  const handleEdit = (record) => {
    setEditId(record._id);
    setShowForm(true);
  };

  const handleDeleteOpen = (record) => {
    setSelectedRole(record);
    setOpenDelete(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRole) return;
    setDeleteLoading(true);
    try {
      const res = await deleteRole(selectedRole._id);
      if (res?.status) {
        toast.success(res?.message || "Role deleted successfully", { toastId: 'role-delete-success' });
        setOpenDelete(false);
        setSelectedRole(null);
        fetchRoles();
      } else {
        toast.error(res?.message || "Failed to delete role", { toastId: 'role-delete-error' });
      }
    } catch (e) {
      toast.error(e?.data?.message || "Something went wrong", { toastId: 'role-delete-error' });
    }
    setDeleteLoading(false);
  };

  const handleBack = () => {
    setShowForm(false);
    setEditId(null);
    fetchRoles();
  };

  if (showForm) {
    return (
      <>
        <SidebarNav />
        <div className="page-wrapper">
          <RoleForm onBack={handleBack} editId={editId} />
        </div>
      </>
    );
  }

  const actionColumn = {
    key: "__action",
    title: "Action",
    className: "text-end",
    render: (_, record) => (
      <div className="text-end">
        {record.roleType !== 1 && canUpdate("role") && (
          <a
            className="me-1 btn btn-sm bg-success-light"
            onClick={() => handleEdit(record)}
          >
            <i className="fe fe-pencil"></i> Edit
          </a>
        )}
        {record.roleType !== 1 && canDelete("role") && (
          <a
            className="me-1 btn btn-sm bg-danger-light"
            onClick={() => handleDeleteOpen(record)}
          >
            <i className="fe fe-trash"></i> Delete
          </a>
        )}
      </div>
    ),
  };

  const columns = [...dynamicColumns, actionColumn];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Role Management</h3>
              </div>
              <div className="col-auto text-end">
                {canCreate("role") && (
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setEditId(null);
                      setShowForm(true);
                    }}
                  >
                    Add Role
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="list-layout">
            <div className="list-layout__main">
              <div className="card card-table">
                <div className="card-body">
                  <TableToolbar
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                    total={total}
                    page={page}
                    visibleColumnKeys={visibleColumnKeys}
                    allColumns={ALL_COLUMNS}
                    toggleColumn={toggleColumn}
                    resetColumns={resetColumns}
                    onDownload={handleDownload}
                    downloadLoading={downloadLoading}
                  />
                  <div className="table-responsive">
                    {loading ? (
                      <div className="his-spin">
                        <Spin
                          size="large"
                          style={{ position: "relative", top: "35%" }}
                        />
                      </div>
                    ) : (
                      <Table
                        pagination={false}
                        style={{ overflowX: "auto" }}
                        dataSource={roleList}
                        columns={columns}
                        rowKey={(record) => record._id}
                      />
                    )}
                    {total > pageSize && (
                      <CustomPagination
                        key={`pg-${pageSize}`}
                        totalTabs={total}
                        tabsPerPage={pageSize}
                        handleChange={setPage}
                        className={`${
                          loading ? "display-hide" : "display-flex"
                        }`}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="list-layout__filters">
              <FilterPanel
                count={total}
                onClear={() => {
                  setSearchTerm("");
                }}
              >
                <FilterPanel.Field label="Search">
                  <input
                    type="text"
                    placeholder="Role name"
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(sanitizeInput(e.target.value))
                    }
                  />
                </FilterPanel.Field>
              </FilterPanel>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={openDelete}>
          <Box sx={styleDelete}>
            <h5 style={{ marginBottom: "16px" }}>Delete Role</h5>
            <p>
              Are you sure you want to delete the role{" "}
              <strong>{selectedRole?.name}</strong>?
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                marginTop: "24px",
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => setOpenDelete(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
              >
                {deleteLoading ? <Spinner size="sm" /> : "Delete"}
              </button>
            </div>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default RoleManagement;
